<template>
    <div class="white-box">
        <transition
            name="opacity"
            mode="out-in"
        >
            <Skeletons v-if="isGettingData" />
            <div v-else>
                <h4 class="mb-1.5 md:mb-5">
                    請選擇您欲贖回之投資標的
                </h4>
                <div
                    v-if="!!msgList.length"
                    class="pt-0.5 text-warning text-sm xs:text-base mb-2.5"
                >
                    <p
                        v-for="(msgStr,idx) in msgList"
                        :key="idx"
                        class="mb-1"
                    >
                        {{ msgStr }}
                    </p>
                </div>
                <div
                    v-for="(target, idx) in data"
                    :key="idx"
                    class="border-t last:border-b border-gray-200 border-solid"
                >
                    <CollapseTarget
                        v-model:field="data[idx]"
                        :idx="idx"
                        :currencyData="{currencyText:policy.currencyText,currency:policy.currency}"
                        redeemAmountRule="float"
                        floatNum="5"
                        openButtonName="贖回"
                    >
                        <template #data>
                            <div
                                class="grid grid-cols-2 md:grid-cols-5 gap-x-4 md:mb-0"
                                :class="{'mb-2.5':!target.isRedeem}"
                            >
                                <div class="col-span-2 border-b border-dashed border-gray-200 md:border-0 flex md:block justify-between items-baseline flex-wrap py-2 md:pt-2.5">
                                    <ContentAndTitle
                                        :title="`預估帳戶價值(${policy.currencyText})`"
                                        :content="target.acnoValueDisplay"
                                    />
                                </div>
                                <div
                                    class="col-span-2 border-dashed border-gray-200 md:border-0 flex md:block justify-between items-baseline flex-wrap py-2 md:pt-2.5 "
                                    :class="{'border-b mb-[2.625rem] md:mb-0':!target.isRedeem}"
                                >
                                    <ContentAndTitle
                                        title="持有單位數"
                                        :content="target.unitString"
                                    />
                                </div>
                            </div>
                        </template>
                    </CollapseTarget>
                </div>
            </div>
        </transition>
    </div>
    <Bottombar>
        <template #top>
            <div class="text-xs py-1">
                預估贖回總金額({{ policy.currencyText }})：
            </div>
            <div class="text-base xs:text-lg py-1">
                {{ addComma(transformSum) }}
            </div>
        </template>
        <template #center>
            預估贖回總金額({{ policy.currencyText }})：{{ addComma(transformSum) }}
        </template>
        <template #right>
            <ButtonNextService
                v-model:isSubmitOk="isSubmitOk"
                :disabled="!data.some(i => i.isRedeem)"
                @click="validateData"
            />
        </template>
    </Bottombar>
    <!-- 後端驗證錯誤訊息燈箱 -->
    <PopupBase v-model:visiblePopup="visiblePopup">
        <template #title>
            提示
        </template>
        <template #content>
            <ul
                v-if="errorResponse.length>0"
                :class="{'list-decimal space-y-2 ml-6 ':errorResponse.length>1}"
            >
                <li
                    v-for="(item, index) in errorResponse"
                    :key="index"
                >
                    <p class="text-base">
                        {{ item.errorMessage }}
                    </p>
                </li>
            </ul>
        </template>
        <template #action>
            <button
                class="btn gradient-primary rounded-full"
                @click="closePopup"
            >
                確定
            </button>
        </template>
    </PopupBase>
    <Loading :is-loading="isLoading" />
</template>

<script>
import { ref, computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { getInfoA7, submitA7 , fundCodeInfo } from '@/apis/A7.js';
import { addComma } from '@/utils/number/addComma';
import CollapseTarget from '@/components/collapse/CollapseTarget.vue';
import Bottombar from '@/components/layout/Bottombar.vue';
import Loading from '@/components/Loading.vue';
import ContentAndTitle from '@/components/text/ContentAndTitle.vue';
import ButtonNextService from '@/components/buttons/ButtonNextService.vue';
import PopupBase from '@/components/dialogs/Popup.vue';
import Decimal from 'decimal.js';
import dialogContnet from '@/json/dialogContent.js';
import Skeletons from './Skeletons.vue';
import _ from 'lodash';

export default {
    components: {
        CollapseTarget,
        Bottombar,
        Loading,
        ContentAndTitle,
        PopupBase,
        ButtonNextService,
        Skeletons
    },
    setup() {
        const $vfm = inject('$vfm');
        const route = useRoute();
        const store = useStore();
        const isSubmitOk = ref(false);
        const { code } = route.meta;
        const data = ref([]);
        const msgList = ref([]);
        const isLoadingData = ref(false);
        const isLoading = ref(false);
        const isGettingData = ref(false);
        /* 保單資料 */
        const policy = computed(() => store.state.policy.policy);
        const result = computed(() => {
            const vuexData = store.state.services?.changeItemsResult[code];
            return vuexData ? _.cloneDeep(vuexData) : undefined;
        });

        const visiblePopup = ref(false);
        const errorResponse = ref([]);

        const getData = () => {
            isGettingData.value = true;
            getInfoA7().then((res) => {
                if (res.data) {
                    msgList.value = res.data.msgList;
                    data.value = res.data.terminateList.map(i => {
                        i.acnoValueDisplay = addComma(i.acnoValue);
                        // 是否執行轉出or贖回，預設都不要
                        i.isRedeem = false;
                        // 轉出or贖回形式 UNIT or PERCENT，預設依單位數
                        i.redeemBy = 'UNIT';
                        // 轉出or贖回單位數
                        i.redeemUnit = 0;
                        // 轉出or贖回比例，正整數
                        i.redeemPercent = 0;
                        // 預估轉入金額
                        i.redeemAmount = 0;
                        // vuex 有資料
                        if (result.value) {
                            result.value.redeemDtoList.forEach(j => {
                                // mapping資料
                                if (i.fundCode === j.fundCode) {
                                    // 依比例或依單位數有值
                                    if (!!j.redeemUnit || !!j.redeemNum) {
                                        i.isRedeem = true;
                                        i.redeemBy = j.redeemUnit ? 'UNIT' : 'PERCENT';
                                        i.redeemUnit = j.redeemUnit ? j.redeemUnit : 0;
                                        i.redeemPercent = j.redeemNum ? j.redeemNum : 0;
                                    }
                                }
                            });
                        }
                        return i;
                    });
                }
            }).finally(() => {
                isGettingData.value = false;
            });
        };
        getData();

        // 轉出金額總和
        const transformSum = computed(() => {
            let sum = new Decimal(0);
            data.value.forEach(i => {
                sum = sum.plus(i.redeemAmount);
            });
            return parseFloat(sum);
        });

        const validateData = () => {
            isLoading.value = true;
            data.value.map(i => {
                // 如果要贖回
                if (i.isRedeem) {
                    // 如果選擇單位數
                    if (i.redeemBy === 'UNIT' && !i.redeemUnit) i.errorMsgRedeemUnit = '請輸入數值';
                    // 如果選擇比例
                    if (i.redeemBy === 'PERCENT' && !i.redeemPercent) i.errorMsgRedeemPercent = '請輸入介於1~100之數字';
                }
            });

            const hasError = data.value.some(i => !!i.errorMsgRedeemUnit || !!i.errorMsgRedeemPercent);

            if (!hasError) {
                goNext();
            } else {
                modalShow(dialogContnet.validateMsg);
                isLoading.value = false;
            }
        };

        const goNext = () => {
            const returnData = {};
            const tempArr = [];
            data.value.forEach(i => {
                if (i.isRedeem) {
                    const obj = {};
                    obj.fundCode = i.fundCode;
                    obj.redeemUnit = i.redeemUnit;
                    obj.redeemNum = i.redeemPercent;
                    if (!i.disable) tempArr.push(obj);
                }
            });
            returnData.redeemDtoList = tempArr;


            const fundCode = [...returnData.redeemDtoList.map(item => ({
                fundCode : item.fundCode,
                kind : 0
            }))]

            const paload = {fundCodeOutList : fundCode}
            
            fundCodeInfo(paload).then(res => {
                const params = {
                    title: '提示',
                    // content: `基金代號［${str}］不可同時轉入與轉出。`,
                    content : `${res.data.fundCodeMsg}`.replace(/\n/g,"<br>"),
                    confirmButton: '確認',
                    cancelButton: null,
                    closeicon: false,
                }
                const hasFundCodeMsg = res.data.fundCodeMsg && res.data.fundCodeMsg.trim() !== "";
                if(hasFundCodeMsg){
                    modalShow(params,callA7);
                }else{
                    callA7();
                }
                
            });


            const callA7 = () => {
                submitA7(returnData).then(res => {
                isSubmitOk.value = true;
                const storeData = { ...returnData };
                store.dispatch('services/setChangeItemsResult', { code, value: storeData });
            }).catch((err) => {
                const errData = err.data?.details || [];
                if (errData[0]) {
                    visiblePopup.value = true;
                    errorResponse.value = errData;
                }
            }).finally(() => {
                isLoading.value = false;
            });
            }
        };

        const modalShow = (params,vl) => {
            $vfm.show({
                component: 'DialogBase',
                params,
                on: {
                    confirm() {
                        $vfm.hide('DialogBase');
                        if(vl())vl();
                    },
                    closed() {
                        $vfm.hide('DialogBase');
                    }
                },
            });
        };

        const closePopup = () => {
            visiblePopup.value = false;
            errorResponse.value = [];
        };
        return {
            data,
            policy,
            isLoadingData,
            isLoading,
            isGettingData,
            transformSum,
            visiblePopup,
            errorResponse,
            validateData,
            closePopup,
            msgList,
            addComma,
            isSubmitOk
        };
    },
};
</script>
